/**
 * PATH: packages/kanban-host/src/allocation/MoveToBoardLaneList.tsx
 * FILE_ID: KANBANHOST-MOVETOBOARDLANELIST-001
 * PURPOSE: Lane sub-view for the inline "Move to another board"
 *          picker. Once the operator drills into a target board this
 *          lists that board's lanes so the email lands on a SPECIFIC
 *          lane instead of being silently dropped on the board's
 *          left-most (default) lane.
 * WHY:     Operator report 2026-06-10 (Newmark): *"it doesn't have
 *          lanes to triage in this email … I don't have board
 *          sub-menus here to allocate it to there"*. The inline board
 *          picker (MoveToBoardPicker) only listed boards and dumped
 *          the email on the default lane. The right-click ContextMenu
 *          submenu already drilled board→lane (AllocateToKanbanSubMenu
 *          / LaneList); this brings the same drill-down to the inline
 *          chip surface, rendering lanes straight from the board-list
 *          payload (no extra round-trip → instant).
 * FLOW:
 *   ┌─────────────┐   ┌────────────────────┐   ┌────────────────┐
 *   │ board.lanes │ → │ sortLanesForPicker │ → │ lane rows →    │
 *   │ (cached)    │   │ (drop archived,    │   │ onPick(laneId) │
 *   └─────────────┘   │  sort by position) │   └────────────────┘
 *                     └────────────────────┘
 * DEPENDENCIES:
 *   - @quotech/ui/kanban : Lane type + getLaneColor swatch resolver
 *   - @quotech/tenant-shared/lib/utils : cn
 * RELATED:
 *   - ./MoveToBoardPicker.tsx : parent (owns board selection + onPick)
 *   - ./AllocateToKanbanSubMenu.tsx::LaneList : the ContextMenu
 *     equivalent (same sort/star rules, different render surface)
 */

import { ChevronLeft, Star } from 'lucide-react'

import { cn } from '@quotech/tenant-shared/lib/utils'

import type { Lane } from '@quotech/ui/kanban'
import { getLaneColor } from '@quotech/ui/kanban'

// ──────────────────────────────────────────────────────────
// Pure helper (I → O) — unit-tested without rendering React
// ──────────────────────────────────────────────────────────

/**
 * Order a board's lanes for the picker exactly like the rail + the
 * right-click LaneList: drop archived lanes, then sort by `position`
 * ascending so the left-most lane (Triage / Backlog) leads. Returns a
 * fresh array — never mutates the input.
 */
export function sortLanesForPicker(lanes: Lane[] | undefined | null): Lane[] {
  if (!lanes || lanes.length === 0) return []
  return lanes
    .filter((l) => !l.archived)
    .slice()
    .sort((a, b) => a.position - b.position)
}

// ──────────────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────────────

export interface MoveToBoardLaneListProps {
  /** Cleaned board name shown in the back-header breadcrumb. */
  boardName: string
  /** Board swatch dot beside the back-header name. */
  boardSwatch: string | null
  /** Already-sorted, non-archived lanes (see sortLanesForPicker). */
  lanes: Lane[]
  /** Left-most lane id — gets a star so the operator can tell where a
   *  plain "drop on board" would have landed. */
  defaultLaneId: string | null
  /** Lanes are fetched lazily on drill-in now (no longer embedded in
   *  the board-list payload), so show a spinner row instead of the
   *  "no lanes" empty state while the fetch is in flight. */
  isLoading?: boolean
  /** Mirrors the parent mutation-in-flight flag. */
  busy: boolean
  /** Back to the board list. */
  onBack: () => void
  /** Operator chose a lane — parent performs the allocate. */
  onPick: (laneId: string) => void
}

export function MoveToBoardLaneList({
  boardName,
  boardSwatch,
  lanes,
  defaultLaneId,
  isLoading = false,
  busy,
  onBack,
  onPick,
}: MoveToBoardLaneListProps) {
  return (
    <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 w-full px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/70 font-semibold hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-3 w-3 shrink-0" aria-hidden />
        <span
          className="h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: boardSwatch || '#94a3b8' }}
          aria-hidden
        />
        <span className="truncate">{boardName}</span>
      </button>

      <div className="px-2 pb-1 text-[10px] text-muted-foreground/70">
        Pick a lane to drop this email on
      </div>

      <div className="max-h-[220px] overflow-y-auto">
        {isLoading && lanes.length === 0 ? (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            Loading lanes…
          </div>
        ) : lanes.length === 0 ? (
          <div className="px-2 py-1 text-[11px] text-muted-foreground">
            This board has no lanes — add one in Kanban first.
          </div>
        ) : (
          lanes.map((lane) => (
            <button
              key={lane.id}
              type="button"
              disabled={busy}
              onClick={() => onPick(lane.id)}
              title={`Move to ${boardName} · ${lane.name}`}
              className={cn(
                'flex items-center gap-2 w-full px-2 py-1 text-[11px] text-left rounded transition-colors',
                'hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <span
                className="h-2 w-2 rounded-full shrink-0"
                style={{ backgroundColor: getLaneColor(lane) || '#94a3b8' }}
                aria-hidden
              />
              <span className="flex-1 truncate">{lane.name}</span>
              {typeof lane.card_count === 'number' && (
                <span className="text-[9px] text-muted-foreground/70 shrink-0">
                  {lane.card_count}
                </span>
              )}
              {lane.id === defaultLaneId && (
                <Star
                  className="h-2.5 w-2.5 text-amber-500 shrink-0"
                  aria-label="Default lane"
                />
              )}
            </button>
          ))
        )}
      </div>
    </div>
  )
}
